// docs/22 O4/B2 — the opening cinematics' script and beats, free of React so vitest can read them
// against the template story (opening.test.ts).
import { withHeroes, type Genesis, type Line } from '../../core/story/genesis';
import type { Snapshot } from '../../core/sim/types';

export const NAME_MS = 2600;
export const OBJECTIVE_MS = 5200;
export const BORN_MS = 6500;
/** The reading beat once a line is fully typed: a second plus ~a word per 120 ms, capped. */
export const readMs = (text: string) => Math.min(3600, 1100 + text.length * 24);

export const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export type FaceStep = { kind: 'name' } | { kind: 'line'; line: Line; leader: boolean; cry?: boolean } | { kind: 'objective' };

/** The face-off in order: the boss card, the parley (the leader's lines marked), the battle cry, the objective. */
export function faceoffSteps(g: Genesis, heroes: string[]): FaceStep[] {
  const leader = g.battle.leader.name;
  const lines: FaceStep[] = (g.battle.parley ?? []).map((l) => ({ kind: 'line', line: { speaker: withHeroes(l.speaker, heroes), text: withHeroes(l.text, heroes) }, leader: same(l.speaker, leader) }));
  // a beast's "cry" is written as narration (the fen heaves…): it gets the narrator's box, not a speaker
  const beast = g.battle.foe === 'Beast';
  const cry: FaceStep = { kind: 'line', line: { speaker: beast ? '' : leader, text: withHeroes(g.battle.cry, heroes) }, leader: !beast, cry: true };
  return [{ kind: 'name' }, ...lines, cry, { kind: 'objective' }];
}

/** The story's leader on the board: by name (the forge renames the kit's leader), else the snapshot's leader. */
export function leaderUnitOf(snapshot: Snapshot | undefined, g: Genesis | undefined) {
  if (!snapshot || !g) return undefined;
  return snapshot.units.find((u) => !!u.name && same(u.name, g.battle.leader.name)) ?? (snapshot.leaderUnit !== undefined ? snapshot.units.find((u) => u.unitId === snapshot.leaderUnit) : undefined);
}

/** Roughly how long the face-off runs unskipped (ms), at a typewriter speed of `cps`. */
export function faceoffMs(steps: FaceStep[], cps = 60): number {
  return steps.reduce((t, s) => t + (s.kind === 'name' ? NAME_MS : s.kind === 'objective' ? OBJECTIVE_MS : (s.line.text.length / cps) * 1000 + readMs(s.line.text)), 0);
}
